import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { metasPoupanca as metasApi, depositos as depositosApi, userCategories as categoriesApi } from '../lib/api';
import { formatCurrency, parseCurrency, GOAL_TEMPLATES, GOAL_ICON_PALETTE } from '../utils/format';
import {
  calcProjectedDate,
  calcCompoundInterest,
  calcMonthsToReachTarget,
  calcRequiredMonthlyContribution,
} from '../utils/calculations';
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Target,
  Heart,
  Home,
  Plane,
  Gift,
  Shield,
  TrendingUp,
  PiggyBank,
  Calendar,
  Check,
  Calculator,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Car,
  GraduationCap,
  Briefcase,
  Baby,
  PawPrint,
  BookOpen,
  ShoppingBag,
  Smartphone,
  Coffee,
  Music,
  Camera,
  Wrench,
  Star,
  Trophy,
  Building,
  Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import LoadingTimeout from '../components/LoadingTimeout';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { useRegisterRefresh } from '../context/RefreshContext';
import { useAuth } from '../context/AuthContext';
import { canAddMeta, getPlanLimits } from '../utils/planLimits';
import { UpgradePrompt, LimitBadge } from '../components/UpgradePrompt';

const iconMap = {
  Heart, Home, Plane, Gift, Shield, TrendingUp, Target,
  Car, GraduationCap, Briefcase, Baby, PawPrint, BookOpen,
  ShoppingBag, Smartphone, Coffee, Music, Camera, Wrench,
  Star, Trophy, Building, Wallet, PiggyBank,
};

function renderMonthsLabel(months) {
  if (!isFinite(months)) return '—';
  if (months === 0) return 'agora mesmo';
  if (months < 12) return `${months} ${months === 1 ? 'mês' : 'meses'}`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  const yLabel = `${y} ${y === 1 ? 'ano' : 'anos'}`;
  if (m === 0) return yLabel;
  return `${yLabel} e ${m} ${m === 1 ? 'mês' : 'meses'}`;
}

function monthsUntil(yyyyMM) {
  if (!yyyyMM) return null;
  const [y, m] = yyyyMM.split('-').map(Number);
  const now = new Date();
  const diff = (y * 12 + m) - (now.getFullYear() * 12 + now.getMonth() + 1);
  return diff > 0 ? diff : null;
}

// Inline simulation shown inside the create/edit modal, reacting to form values live.
function ModalSimulation({ valorAlvo, valorAtual, contribuicaoMensal, dataAlvo }) {
  const [rateInput, setRateInput] = useState('0');

  const target = parseCurrency(valorAlvo) || 0;
  const monthly = parseCurrency(contribuicaoMensal) || 0;
  const deadlineMonths = monthsUntil(dataAlvo);

  const annualRate = useMemo(() => {
    const v = parseFloat((rateInput || '').replace(',', '.'));
    return isNaN(v) || v < 0 ? 0 : v;
  }, [rateInput]);

  // How much to save per month to hit the deadline
  const requiredMonthly = useMemo(() => {
    if (target <= 0 || !deadlineMonths) return null;
    const amt = calcRequiredMonthlyContribution(target, valorAtual, deadlineMonths, annualRate);
    return isFinite(amt) && amt > 0 ? amt : null;
  }, [target, valorAtual, deadlineMonths, annualRate]);

  // How long to reach goal at the given monthly contribution
  const projection = useMemo(() => {
    if (target <= 0 || monthly <= 0) return null;
    const months = calcMonthsToReachTarget(target, valorAtual, monthly, annualRate);
    if (!isFinite(months)) return { months: Infinity };
    const years = months / 12;
    const futureValue = calcCompoundInterest(valorAtual, monthly, annualRate, years);
    const totalContributed = valorAtual + monthly * months;
    return {
      months,
      date: calcProjectedDate(months),
      totalContributed,
      interestEarned: Math.max(futureValue - totalContributed, 0),
    };
  }, [target, valorAtual, monthly, annualRate]);

  if (target <= 0) return null;

  const hasAnyInput = deadlineMonths || monthly > 0;
  if (!hasAnyInput) return null;

  return (
    <div className="border-2 border-amber-200 rounded-xl p-4 bg-amber-50 space-y-3">
      <p className="text-base font-semibold text-amber-800 flex items-center gap-2">
        <Calculator size={18} />
        Simulação
      </p>
      <div className="space-y-1">
        <Label className="text-sm font-semibold text-slate-600">Taxa de rendimento anual (%)</Label>
        <Input
          inputMode="decimal"
          value={rateInput}
          onChange={(e) => setRateInput(e.target.value)}
          placeholder="0"
          className="h-12 text-base border-2 bg-white"
        />
        <p className="text-xs text-slate-500">Deixe em 0 se não vai investir o dinheiro.</p>
      </div>

      {/* Deadline → required monthly */}
      {deadlineMonths && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-3 space-y-1">
          <p className="text-xs font-semibold text-blue-700">
            Para atingir em {renderMonthsLabel(deadlineMonths)} ({deadlineMonths} meses):
          </p>
          {requiredMonthly ? (
            <p className="text-lg font-bold text-blue-800">
              Guardar {formatCurrency(requiredMonthly)}/mês
            </p>
          ) : (
            <p className="text-sm text-blue-700">Prazo muito curto para o valor alvo.</p>
          )}
        </div>
      )}

      {/* Monthly contribution → projected date */}
      {monthly > 0 && (
        !isFinite(projection?.months) ? (
          <div className="bg-amber-100 border-2 border-amber-300 rounded-xl p-3 text-center">
            <p className="text-sm text-amber-800">Com esse valor a meta não é alcançável. Tente guardar mais por mês.</p>
          </div>
        ) : projection?.months > 120 ? (
          <div className="bg-amber-100 border-2 border-amber-300 rounded-xl p-3 text-center space-y-1">
            <p className="text-sm font-bold text-amber-800">Vai levar mais de 10 anos.</p>
            <p className="text-xs text-amber-700">Considere aumentar a contribuição mensal.</p>
          </div>
        ) : projection ? (
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-3">
              <p className="text-xs font-semibold text-green-700">Guardando {formatCurrency(monthly)}/mês:</p>
              <p className="text-base font-bold text-green-700 leading-tight">{renderMonthsLabel(projection.months)}</p>
              {projection.date && (
                <p className="text-xs text-green-800 mt-0.5">{projection.date.monthName} de {projection.date.year}</p>
              )}
            </div>
            <div className="bg-white border-2 border-slate-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-slate-600">Total guardado:</p>
              <p className="text-base font-bold text-[#1B2A4A]">{formatCurrency(projection.totalContributed)}</p>
              {annualRate > 0 && projection.interestEarned > 0 && (
                <p className="text-xs text-green-700 mt-0.5 flex items-center gap-1">
                  <Sparkles size={12} />{formatCurrency(projection.interestEarned)} em juros
                </p>
              )}
            </div>
          </div>
        ) : null
      )}
    </div>
  );
}

function GoalProjection({ meta }) {
  const [open, setOpen] = useState(false);
  const [monthlyInput, setMonthlyInput] = useState('0');
  const [rateInput, setRateInput] = useState('0');

  const remaining = Math.max((meta.valor_alvo || 0) - (meta.valor_atual || 0), 0);
  const isComplete = remaining <= 0;

  const monthlyContribution = useMemo(() => {
    const parsed = parseCurrency(monthlyInput);
    return isNaN(parsed) ? 0 : parsed;
  }, [monthlyInput]);

  const annualRate = useMemo(() => {
    const v = parseFloat((rateInput || '').toString().replace(',', '.'));
    return isNaN(v) || v < 0 ? 0 : v;
  }, [rateInput]);

  const projection = useMemo(() => {
    if (isComplete || monthlyContribution <= 0) return null;

    const months = calcMonthsToReachTarget(
      meta.valor_alvo || 0,
      meta.valor_atual || 0,
      monthlyContribution,
      annualRate
    );
    if (!isFinite(months)) return { months: Infinity };

    const years = months / 12;
    const futureValue = calcCompoundInterest(
      meta.valor_atual || 0,
      monthlyContribution,
      annualRate,
      years
    );
    const totalContributed = (meta.valor_atual || 0) + monthlyContribution * months;
    const interestEarned = Math.max(futureValue - totalContributed, 0);

    const doubleMonths = calcMonthsToReachTarget(
      meta.valor_alvo || 0,
      meta.valor_atual || 0,
      monthlyContribution * 2,
      annualRate
    );

    return {
      months,
      date: calcProjectedDate(months),
      totalContributed,
      interestEarned,
      doubleContribution: monthlyContribution * 2,
      doubleMonths,
    };
  }, [isComplete, monthlyContribution, annualRate, meta.valor_alvo, meta.valor_atual]);

  return (
    <div className="pt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 h-12 rounded-xl border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 text-[#1B2A4A] font-semibold transition-all active:scale-[0.99]"
        data-testid={`projection-toggle-${meta.id}`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-base">
          <Calculator size={20} className="text-amber-600" />
          📊 Simular quanto tempo vai levar
        </span>
        {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {open && (
        <div
          className="mt-3 p-4 bg-white border-2 border-amber-200 rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-2 duration-300"
          data-testid={`projection-panel-${meta.id}`}
        >
          {isComplete ? (
            <p className="text-center text-lg font-bold text-green-700 py-4">
              🎉 Meta já atingida!
            </p>
          ) : (
            <>
              {/* Quanto falta */}
              <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-3">
                <p className="text-sm text-slate-500">Quanto falta</p>
                <p className="text-xl font-bold text-[#1B2A4A]">
                  {formatCurrency(remaining)}
                </p>
              </div>

              {/* Inputs */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  Quanto posso guardar por mês (R$)
                </Label>
                <Input
                  inputMode="decimal"
                  value={monthlyInput}
                  onChange={(e) => setMonthlyInput(e.target.value)}
                  placeholder="0,00"
                  className="h-14 text-lg border-2"
                  data-testid={`projection-monthly-${meta.id}`}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-semibold">
                  Taxa de rendimento anual (%)
                </Label>
                <Input
                  inputMode="decimal"
                  value={rateInput}
                  onChange={(e) => setRateInput(e.target.value)}
                  placeholder="0"
                  className="h-14 text-lg border-2"
                  data-testid={`projection-rate-${meta.id}`}
                />
                <p className="text-sm text-slate-500">
                  Deixe em 0 se não vai investir o dinheiro.
                </p>
              </div>

              {/* Results */}
              {monthlyContribution <= 0 ? (
                <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 text-center">
                  <p className="text-base text-slate-600">
                    Digite um valor para simular
                  </p>
                </div>
              ) : !isFinite(projection.months) ? (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 text-center">
                  <p className="text-base text-amber-800">
                    Com esse valor, a meta não é alcançada. Tente guardar um
                    pouco mais por mês.
                  </p>
                </div>
              ) : projection.months > 120 ? (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 text-center space-y-2">
                  <p className="text-lg font-bold text-amber-800">
                    Vai levar mais de 10 anos.
                  </p>
                  <p className="text-base text-amber-700">
                    Considere guardar um pouco mais por mês.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Time to reach */}
                  <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                    <p className="text-sm font-semibold text-green-700 mb-1">
                      Você vai atingir sua meta em:
                    </p>
                    <p className="text-2xl font-bold text-green-700 leading-tight">
                      {renderMonthsLabel(projection.months)}
                    </p>
                    {projection.date && (
                      <p className="text-base text-green-800 mt-1">
                        Em {projection.date.monthName} de {projection.date.year}
                      </p>
                    )}
                  </div>

                  {/* Total saved */}
                  <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-slate-600 mb-1">
                      Total que vai guardar:
                    </p>
                    <p className="text-xl font-bold text-[#1B2A4A]">
                      {formatCurrency(projection.totalContributed)}
                    </p>
                    {annualRate > 0 && projection.interestEarned > 0 && (
                      <p className="text-sm text-green-700 mt-1 flex items-center gap-1">
                        <Sparkles size={14} />
                        Dos quais {formatCurrency(projection.interestEarned)}{' '}
                        em rendimentos
                      </p>
                    )}
                  </div>

                  {/* What if: double */}
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-amber-800 mb-1">
                      Se guardar o dobro ({formatCurrency(projection.doubleContribution)}/mês):
                    </p>
                    <p className="text-lg font-bold text-amber-900">
                      {isFinite(projection.doubleMonths)
                        ? `Em ${renderMonthsLabel(projection.doubleMonths)}`
                        : '—'}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MetasPage() {
  const { user } = useAuth();
  const plan = user?.plan || 'gratuito';
  const limits = getPlanLimits(plan);
  const [metas, setMetas] = useState([]);
  const [userCategories, setUserCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [showDepositoModal, setShowDepositoModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingMeta, setEditingMeta] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Category form (inside the manager dialog)
  const [categoryForm, setCategoryForm] = useState({ nome: '', icon: 'Target' });
  const [categoryError, setCategoryError] = useState('');
  
  // Form states
  const [metaForm, setMetaForm] = useState({
    nome_meta: '',
    template: 'outros',
    icon: 'Target',
    valor_alvo: '',
    contribuicao_mensal: '',
    prazo_meses: '',
    data_alvo: '',
  });
  
  const [depositoForm, setDepositoForm] = useState({
    valor: '',
    data_deposito: new Date().toISOString().split('T')[0],
    observacao: '',
  });

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [metasRes, catsRes] = await Promise.all([
        metasApi.list(),
        categoriesApi.list('meta'),
      ]);
      setMetas(metasRes);
      setUserCategories(catsRes);
    } catch (error) {
      console.error('Failed to load metas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await categoriesApi.list('meta');
      setUserCategories(cats);
    } catch (error) {
      console.error('Failed to load goal categories:', error);
    }
  }, []);

  const handleSaveCategory = async () => {
    setCategoryError('');
    const nome = categoryForm.nome.trim();
    if (!nome) {
      setCategoryError('Digite um nome para a categoria.');
      return;
    }
    try {
      const created = await categoriesApi.create(nome, 'meta', categoryForm.icon || 'Target');
      setCategoryForm({ nome: '', icon: 'Target' });
      await loadCategories();
      // If launched from the meta modal, auto-select the new category and close the manager
      if (showMetaModal) {
        setMetaForm(prev => ({
          ...prev,
          template: `custom_${created.id}`,
          nome_meta: created.nome,
          icon: created.icon || 'Target',
        }));
        setShowCategoryManager(false);
      }
    } catch (error) {
      // Most likely the unique (user_id, nome, tipo) constraint
      setCategoryError(
        error?.code === '23505'
          ? 'Você já tem uma categoria com esse nome.'
          : 'Não foi possível salvar. Tente novamente.'
      );
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await categoriesApi.delete(id);
      await loadCategories();
    } catch (error) {
      console.error('Failed to delete goal category:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { timedOut, resetTimeout } = useLoadingTimeout(loading, loadData);
  useRegisterRefresh(loadData);

  // Handle meta
  const handleSaveMeta = async () => {
    // Enforce tier limit for new metas
    if (!editingMeta && !canAddMeta(plan, metas.length)) {
      return;
    }

    const template = GOAL_TEMPLATES.find(t => t.id === metaForm.template);
    const deadlineMonths = monthsUntil(metaForm.data_alvo);
    const data = {
      nome_meta: metaForm.nome_meta || template?.label || 'Nova Meta',
      valor_alvo: parseCurrency(metaForm.valor_alvo),
      contribuicao_mensal: parseCurrency(metaForm.contribuicao_mensal) || 0,
      prazo_meses: deadlineMonths || (metaForm.prazo_meses ? parseInt(metaForm.prazo_meses) : null),
      icon: metaForm.icon || 'Target',
      data_alvo: metaForm.data_alvo ? `${metaForm.data_alvo}-01` : null,
    };

    try {
      if (editingMeta) {
        await metasApi.update(editingMeta.id, data);
      } else {
        await metasApi.create(data);
      }
      setShowMetaModal(false);
      setEditingMeta(null);
      setMetaForm({ nome_meta: '', template: 'outros', icon: 'Target', valor_alvo: '', contribuicao_mensal: '', prazo_meses: '', data_alvo: '' });
      loadData();
    } catch (error) {
      console.error('Failed to save meta:', error);
    }
  };

  const handleEditMeta = (meta) => {
    setEditingMeta(meta);
    const matchedTemplate = GOAL_TEMPLATES.find(t => t.label === meta.nome_meta);
    const matchedCategory = userCategories.find(c => c.nome === meta.nome_meta);
    let template = 'outros';
    if (matchedTemplate) template = matchedTemplate.id;
    else if (matchedCategory) template = `custom_${matchedCategory.id}`;
    setMetaForm({
      nome_meta: meta.nome_meta,
      template,
      icon: meta.icon || matchedTemplate?.icon || matchedCategory?.icon || 'Target',
      valor_alvo: meta.valor_alvo.toString().replace('.', ','),
      contribuicao_mensal: meta.contribuicao_mensal?.toString().replace('.', ',') || '',
      prazo_meses: meta.prazo_meses?.toString() || '',
      data_alvo: meta.data_alvo ? meta.data_alvo.slice(0, 7) : '',
    });
    setShowMetaModal(true);
  };

  const handleDeleteMeta = async (id) => {
    try {
      await metasApi.delete(id);
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete meta:', error);
    }
  };

  // Handle deposito
  const handleSaveDeposito = async () => {
    if (!selectedMeta) return;
    
    const data = {
      meta_id: selectedMeta.id,
      valor: parseCurrency(depositoForm.valor),
      data_deposito: depositoForm.data_deposito,
      observacao: depositoForm.observacao || null,
    };

    try {
      await depositosApi.create(data);
      setShowDepositoModal(false);
      setSelectedMeta(null);
      setDepositoForm({ valor: '', data_deposito: new Date().toISOString().split('T')[0], observacao: '' });
      loadData();
    } catch (error) {
      console.error('Failed to save deposito:', error);
    }
  };

  const openDepositoModal = (meta) => {
    setSelectedMeta(meta);
    setDepositoForm({ valor: '', data_deposito: new Date().toISOString().split('T')[0], observacao: '' });
    setShowDepositoModal(true);
  };

  // Resolve icon: prefer the row's stored icon, fall back to template-name match (legacy rows)
  const getIcon = (meta) => {
    if (meta.icon && iconMap[meta.icon]) return iconMap[meta.icon];
    const template = GOAL_TEMPLATES.find(t => t.label === meta.nome_meta || meta.nome_meta.includes(t.label));
    return template ? iconMap[template.icon] || Target : Target;
  };

  // Calculate remaining
  const calcRemaining = (meta) => {
    if (!meta.valor_alvo || !meta.contribuicao_mensal || meta.contribuicao_mensal === 0) return null;
    const remaining = meta.valor_alvo - meta.valor_atual;
    if (remaining <= 0) return 0;
    return Math.ceil(remaining / meta.contribuicao_mensal);
  };

  if (loading) {
    return (
      <Layout>
        {timedOut ? (
          <LoadingTimeout
            timedOut={timedOut}
            onRetry={() => {
              resetTimeout();
              loadData();
            }}
          />
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1B2A4A] border-t-transparent mx-auto mb-4"></div>
              <p className="text-lg font-semibold text-slate-600">Carregando metas...</p>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="metas-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-3">
            <Target size={28} />
            Metas de Poupança
            {limits.metasTotal !== Infinity && (
              <LimitBadge current={metas.length} max={limits.metasTotal} />
            )}
          </h2>
          <Button
            onClick={() => {
              setEditingMeta(null);
              setMetaForm({ nome_meta: '', template: 'outros', icon: 'Target', valor_alvo: '', contribuicao_mensal: '', prazo_meses: '', data_alvo: '' });
              setShowMetaModal(true);
            }}
            disabled={!canAddMeta(plan, metas.length)}
            className="h-12 px-4 bg-[#1B2A4A] hover:bg-[#2C3E50] rounded-xl disabled:opacity-50"
            data-testid="add-meta-btn"
          >
            <Plus size={20} className="mr-2" />
            Nova Meta
          </Button>
        </div>

        {!canAddMeta(plan, metas.length) && (
          <UpgradePrompt feature="criar mais metas de economia" />
        )}

        {/* Metas List */}
        {metas.length === 0 ? (
          <Card className="border-2 border-dashed border-slate-300 bg-slate-50">
            <CardContent className="py-12 text-center">
              <Target size={48} className="mx-auto text-slate-400 mb-4" />
              <h3 className="text-xl font-bold text-slate-600 mb-2">
                Nenhuma meta cadastrada
              </h3>
              <p className="text-lg text-slate-500 mb-4">
                Crie metas para organizar sua poupança
              </p>
              <Button
                onClick={() => setShowMetaModal(true)}
                className="h-12 px-6 bg-[#1B2A4A] hover:bg-[#2C3E50] rounded-xl"
              >
                <Plus size={20} className="mr-2" />
                Criar primeira meta
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {metas.map((meta) => {
              const Icon = getIcon(meta);
              const percent = meta.valor_alvo > 0 ? (meta.valor_atual / meta.valor_alvo) * 100 : 0;
              const isComplete = percent >= 100;
              const remaining = calcRemaining(meta);
              
              return (
                <Card 
                  key={meta.id} 
                  className={`border-2 shadow-lg transition-all ${
                    isComplete 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-slate-200 hover:border-[#1B2A4A]'
                  }`}
                  data-testid={`meta-card-${meta.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isComplete ? 'bg-green-600' : 'bg-[#1B2A4A]'
                        }`}>
                          {isComplete ? (
                            <Check className="w-6 h-6 text-white" />
                          ) : (
                            <Icon className="w-6 h-6 text-white" />
                          )}
                        </div>
                        <div>
                          <CardTitle className={`text-lg font-bold ${isComplete ? 'text-green-700' : 'text-[#1B2A4A]'}`}>
                            {meta.nome_meta}
                          </CardTitle>
                          {isComplete && (
                            <span className="text-sm font-semibold text-green-600">Meta alcançada!</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditMeta(meta)}
                          className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#1B2A4A] hover:bg-slate-100 rounded-lg"
                          aria-label="Editar"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ id: meta.id })}
                          className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-100 rounded-lg"
                          aria-label="Excluir"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Progress */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-lg font-semibold text-slate-600">
                          {formatCurrency(meta.valor_atual)}
                        </span>
                        <span className="text-lg font-semibold text-slate-600">
                          {formatCurrency(meta.valor_alvo)}
                        </span>
                      </div>
                      <Progress 
                        value={Math.min(percent, 100)} 
                        className={`h-6 ${isComplete ? 'bg-green-200' : 'bg-slate-200'}`}
                      />
                      <p className="text-center text-base font-semibold mt-2 text-slate-600">
                        {percent.toFixed(0)}% concluído
                      </p>
                    </div>

                    {/* Info */}
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      {meta.contribuicao_mensal > 0 && (
                        <div className="text-center p-3 bg-slate-100 rounded-xl">
                          <p className="text-sm text-slate-500">Contribuição mensal</p>
                          <p className="text-lg font-bold text-[#1B2A4A]">
                            {formatCurrency(meta.contribuicao_mensal)}
                          </p>
                        </div>
                      )}
                      {remaining !== null && remaining > 0 && (
                        <div className="text-center p-3 bg-slate-100 rounded-xl">
                          <p className="text-sm text-slate-500">Meses restantes</p>
                          <p className="text-lg font-bold text-[#1B2A4A]">
                            {remaining} {remaining === 1 ? 'mês' : 'meses'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Add Deposit Button */}
                    {!isComplete && (
                      <Button
                        onClick={() => openDepositoModal(meta)}
                        className="w-full h-12 bg-green-600 hover:bg-green-700 rounded-xl"
                        data-testid={`add-deposito-btn-${meta.id}`}
                      >
                        <PiggyBank size={20} className="mr-2" />
                        Adicionar Depósito
                      </Button>
                    )}

                    {/* Projection Calculator */}
                    <GoalProjection meta={meta} />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Meta Modal */}
        <Dialog open={showMetaModal} onOpenChange={setShowMetaModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="meta-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1B2A4A]">
                {editingMeta ? 'Editar Meta' : 'Nova Meta de Poupança'}
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600">
                Defina sua meta de economia
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Tipo de Meta</Label>
                <Select
                  value={metaForm.template}
                  onValueChange={(value) => {
                    if (value === '__new_category__') {
                      // Special action: open the create/manage dialog without changing selection
                      setShowCategoryManager(true);
                      return;
                    }
                    if (value === 'outros') {
                      setMetaForm(prev => ({ ...prev, template: value, icon: 'Target' }));
                      return;
                    }
                    const builtIn = GOAL_TEMPLATES.find(t => t.id === value);
                    if (builtIn) {
                      setMetaForm(prev => ({
                        ...prev,
                        template: value,
                        nome_meta: builtIn.label,
                        icon: builtIn.icon,
                      }));
                      return;
                    }
                    const customId = value.startsWith('custom_') ? value.slice(7) : null;
                    const userCat = customId && userCategories.find(c => c.id === customId);
                    if (userCat) {
                      setMetaForm(prev => ({
                        ...prev,
                        template: value,
                        nome_meta: userCat.nome,
                        icon: userCat.icon || 'Target',
                      }));
                    }
                  }}
                >
                  <SelectTrigger className="h-14 text-lg border-2" data-testid="template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GOAL_TEMPLATES.map((template) => (
                      <SelectItem key={template.id} value={template.id} className="text-lg py-3">
                        {template.label}
                      </SelectItem>
                    ))}
                    {userCategories.map((cat) => (
                      <SelectItem key={cat.id} value={`custom_${cat.id}`} className="text-lg py-3">
                        {cat.nome}
                      </SelectItem>
                    ))}
                    <SelectItem
                      value="__new_category__"
                      className="text-lg py-3 font-semibold text-[#1B2A4A] border-t border-slate-200 mt-1"
                      data-testid="new-category-option"
                    >
                      + Nova categoria
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Nome da Meta</Label>
                <Input
                  value={metaForm.nome_meta}
                  onChange={(e) => setMetaForm(prev => ({ ...prev, nome_meta: e.target.value }))}
                  placeholder="Ex: Viagem para Gramado"
                  className="h-14 text-lg border-2"
                  data-testid="nome-meta-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Valor Alvo (R$)</Label>
                <Input
                  value={metaForm.valor_alvo}
                  onChange={(e) => setMetaForm(prev => ({ ...prev, valor_alvo: e.target.value }))}
                  placeholder="10.000,00"
                  className="h-14 text-lg border-2"
                  data-testid="valor-alvo-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Contribuição Mensal (R$) - Opcional</Label>
                <Input
                  value={metaForm.contribuicao_mensal}
                  onChange={(e) => setMetaForm(prev => ({ ...prev, contribuicao_mensal: e.target.value }))}
                  placeholder="500,00"
                  className="h-14 text-lg border-2"
                  data-testid="contribuicao-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Quando quer atingir? - Opcional</Label>
                <Input
                  type="month"
                  value={metaForm.data_alvo}
                  onChange={(e) => setMetaForm(prev => ({ ...prev, data_alvo: e.target.value }))}
                  min={new Date().toISOString().slice(0, 7)}
                  className="h-14 text-lg border-2"
                  data-testid="data-alvo-input"
                />
              </div>
              <ModalSimulation
                valorAlvo={metaForm.valor_alvo}
                valorAtual={editingMeta?.valor_atual || 0}
                contribuicaoMensal={metaForm.contribuicao_mensal}
                dataAlvo={metaForm.data_alvo}
              />
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMetaModal(false)}
                className="h-12 w-full sm:w-auto px-6 text-base border-2"
              >
                <X size={18} className="mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={handleSaveMeta}
                className="h-12 w-full sm:w-auto px-6 text-base bg-[#1B2A4A] hover:bg-[#2C3E50]"
                data-testid="save-meta-btn"
              >
                <Save size={18} className="mr-2" />
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deposito Modal */}
        <Dialog open={showDepositoModal} onOpenChange={setShowDepositoModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="deposito-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-green-700">
                Adicionar Depósito
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600">
                {selectedMeta?.nome_meta}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Valor (R$)</Label>
                <Input
                  value={depositoForm.valor}
                  onChange={(e) => setDepositoForm(prev => ({ ...prev, valor: e.target.value }))}
                  placeholder="0,00"
                  className="h-14 text-lg border-2"
                  data-testid="deposito-valor-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Data do Depósito</Label>
                <Input
                  type="date"
                  value={depositoForm.data_deposito}
                  onChange={(e) => setDepositoForm(prev => ({ ...prev, data_deposito: e.target.value }))}
                  className="h-14 text-lg border-2"
                  data-testid="deposito-data-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-base font-semibold">Observação (Opcional)</Label>
                <Input
                  value={depositoForm.observacao}
                  onChange={(e) => setDepositoForm(prev => ({ ...prev, observacao: e.target.value }))}
                  placeholder="Ex: Economia do mês"
                  className="h-14 text-lg border-2"
                  data-testid="deposito-obs-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDepositoModal(false)}
                className="h-12 w-full sm:w-auto px-6 text-base border-2"
              >
                <X size={18} className="mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={handleSaveDeposito}
                className="h-12 w-full sm:w-auto px-6 text-base bg-green-600 hover:bg-green-700"
                data-testid="save-deposito-btn"
              >
                <PiggyBank size={18} className="mr-2" />
                Depositar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Manager Modal */}
        <Dialog open={showCategoryManager} onOpenChange={(open) => {
          setShowCategoryManager(open);
          if (!open) {
            setCategoryForm({ nome: '', icon: 'Target' });
            setCategoryError('');
          }
        }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="category-manager-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1B2A4A]">
                Categorias de Metas
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600">
                Crie categorias personalizadas para suas metas
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Create form */}
              <div className="space-y-3 border-2 border-slate-200 rounded-xl p-4">
                <Label className="text-base font-semibold">Nova categoria</Label>
                <Input
                  value={categoryForm.nome}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Reforma do quintal"
                  className="h-12 text-lg border-2"
                  data-testid="category-name-input"
                />
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-600">Ícone</Label>
                  <div className="grid grid-cols-6 sm:grid-cols-8 gap-2" data-testid="category-icon-picker">
                    {GOAL_ICON_PALETTE.map((iconName) => {
                      const IconComp = iconMap[iconName] || Target;
                      const selected = categoryForm.icon === iconName;
                      return (
                        <button
                          key={iconName}
                          type="button"
                          onClick={() => setCategoryForm(prev => ({ ...prev, icon: iconName }))}
                          className={`h-10 w-10 flex items-center justify-center rounded-lg border-2 transition-colors ${
                            selected
                              ? 'border-[#1B2A4A] bg-[#1B2A4A] text-white'
                              : 'border-slate-200 text-slate-600 hover:border-[#1B2A4A]'
                          }`}
                          aria-label={iconName}
                          aria-pressed={selected}
                          data-testid={`category-icon-${iconName}`}
                        >
                          <IconComp size={18} />
                        </button>
                      );
                    })}
                  </div>
                </div>
                {categoryError && (
                  <p className="text-sm font-semibold text-red-600">{categoryError}</p>
                )}
                <Button
                  onClick={handleSaveCategory}
                  className="h-12 w-full bg-[#1B2A4A] hover:bg-[#2C3E50]"
                  data-testid="save-category-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Adicionar
                </Button>
              </div>

              {/* List */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Suas categorias</Label>
                {userCategories.length === 0 ? (
                  <p className="text-sm text-slate-500 py-2">
                    Você ainda não criou nenhuma categoria.
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="user-categories-list">
                    {userCategories.map((cat) => {
                      const IconComp = (cat.icon && iconMap[cat.icon]) || Target;
                      return (
                        <li
                          key={cat.id}
                          className="flex items-center justify-between gap-3 px-3 py-2 border-2 border-slate-200 rounded-xl"
                          data-testid={`user-category-${cat.id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-[#1B2A4A] flex items-center justify-center shrink-0">
                              <IconComp size={18} className="text-white" />
                            </div>
                            <span className="font-semibold text-[#1B2A4A] truncate">{cat.nome}</span>
                          </div>
                          <button
                            onClick={() => handleDeleteCategory(cat.id)}
                            className="w-9 h-9 flex items-center justify-center text-red-500 hover:bg-red-100 rounded-lg shrink-0"
                            aria-label={`Excluir ${cat.nome}`}
                            data-testid={`delete-category-${cat.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={() => setShowCategoryManager(false)}
                variant="outline"
                className="h-12 w-full sm:w-auto px-6 text-base border-2"
              >
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-[#1B2A4A]">
                Tem certeza que deseja excluir?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                Esta meta e todos os depósitos serão excluídos permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-3">
              <AlertDialogCancel className="h-12 px-6 text-base border-2">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeleteMeta(deleteConfirm?.id)}
                className="h-12 px-6 text-base bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

export default MetasPage;
